import React, { useContext, useEffect, useRef, useState } from "react";
import { View, StyleSheet, Animated, Easing } from "react-native";
import NtText from "../../components/NtText";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import { fetchUser } from "../../api/user";

/**
 * Sidebar user card — adapted from voiplib-rn's `NtBusinessAccountCard`. Two-
 * zone layout:
 *
 *   - Brand band (top): theme.brand background + two soft decorative circles,
 *     initials avatar, display name + optional username chip.
 *   - Surface zone (bottom): label/value rows for Alias + Email.
 *
 * While the v_users fetch is in flight, the name and email areas render as
 * pulsing skeleton bars so the card layout doesn't jump when the real data
 * lands. Theme + sign-out controls live on the Settings page.
 */
function NtBrandSwitch() {
  const { theme } = useTheme();
  const context = useContext(UserContext);
  const styles = makeStyles(theme);

  const user = context?.user;
  const username = user?.username;
  const domain = user?.domain;
  const userId = user?.userId;

  const [liveName, setLiveName] = useState("");
  const [liveEmail, setLiveEmail] = useState("");
  const [loading, setLoading] = useState(!!(domain && userId));

  // Fade controls — skeleton (loading state) and content (loaded state)
  // cross-fade so the swap feels intentional instead of a hard pop.
  const skeletonOpacity = useRef(new Animated.Value(loading ? 1 : 0)).current;
  const contentOpacity = useRef(new Animated.Value(loading ? 0 : 1)).current;

  useEffect(() => {
    if (!domain || !userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await fetchUser(domain, userId);
        if (cancelled) return;
        if (response?.ok) {
          const raw = response.data?.data || response.data || {};
          const name = raw?.contact?.contact_name_given || "";
          const email = raw?.user_email || raw?.email || "";
          if (name) setLiveName(String(name));
          if (email) setLiveEmail(String(email));
        }
      } catch {
        // Silent — fall back to context.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain, userId]);

  // Cross-fade when the loading flag flips.
  useEffect(() => {
    Animated.parallel([
      Animated.timing(skeletonOpacity, {
        toValue: loading ? 1 : 0,
        duration: 220,
        useNativeDriver: false,
      }),
      Animated.timing(contentOpacity, {
        toValue: loading ? 0 : 1,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: false,
      }),
    ]).start();
  }, [loading]);

  const name = liveName || user?.displayName || username || "Business";
  const email = liveEmail || user?.email || "";
  const initials = getInitials(name);
  const showUsernameChip = !!username && username !== name;

  // Email row renders during loading (as a skeleton) and when an email is
  // present after the fetch; if the user genuinely has no email we just hide
  // that row after the fetch completes.
  const showEmailRow = loading || !!email;

  return (
    <View style={styles.card}>
      {/* Brand band */}
      <View style={styles.topBand}>
        <View style={styles.bgCircle1} />
        <View style={styles.bgCircle2} />

        <View style={styles.topInner}>
          <View style={styles.avatar}>
            <NtText style={styles.avatarText}>{initials}</NtText>
          </View>
          <View style={styles.headInfo}>
            {/* Name — skeleton + real content stacked, cross-faded by opacity */}
            <View style={styles.nameStack}>
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.skeletonOverlay,
                  { opacity: skeletonOpacity },
                ]}
              >
                <Skeleton style={styles.skeletonName} band />
              </Animated.View>
              <Animated.View style={{ opacity: contentOpacity }}>
                <NtText style={styles.name} numberOfLines={1}>
                  {name}
                </NtText>
                {showUsernameChip ? (
                  <View style={styles.usernameChip}>
                    <NtText
                      style={styles.usernameChipText}
                      numberOfLines={1}
                    >
                      {username}
                    </NtText>
                  </View>
                ) : null}
              </Animated.View>
            </View>
          </View>
        </View>
      </View>

      {/* Surface zone */}
      <View>
        {domain ? (
          <View style={[styles.row, showEmailRow && styles.rowDivider]}>
            <NtText style={styles.rowLabel}>ALIAS</NtText>
            <NtText style={styles.rowValue} numberOfLines={1}>
              {domain}
            </NtText>
          </View>
        ) : null}

        {showEmailRow ? (
          <View style={styles.row}>
            <NtText style={styles.rowLabel}>EMAIL</NtText>
            <View style={styles.emailValueWrap}>
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.skeletonOverlay,
                  styles.skeletonOverlayRight,
                  { opacity: skeletonOpacity },
                ]}
              >
                <Skeleton style={styles.skeletonEmail} />
              </Animated.View>
              <Animated.View style={{ opacity: contentOpacity }}>
                <NtText style={styles.rowValue} numberOfLines={1}>
                  {email}
                </NtText>
              </Animated.View>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Inline skeleton bar — a softly pulsing rectangle that signals loading.
 * `band` switches to a light-on-brand variant for use inside the brand band.
 */
function Skeleton({ style, band = false }) {
  const { theme } = useTheme();
  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, []);
  const opacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.45, 0.85],
  });
  return (
    <Animated.View
      style={[
        {
          backgroundColor: band
            ? "rgba(255,255,255,0.35)"
            : theme.surface3,
          borderRadius: 4,
          opacity,
        },
        style,
      ]}
    />
  );
}

function getInitials(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/[\s.@_-]+/);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase() || first.toUpperCase();
}

const makeStyles = (t) =>
  StyleSheet.create({
    card: {
      margin: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: t.borderSoft,
      backgroundColor: t.surface,
      overflow: "hidden",
    },
    topBand: {
      position: "relative",
      overflow: "hidden",
      backgroundColor: t.brand,
      paddingTop: 14,
      paddingBottom: 12,
      paddingHorizontal: 12,
    },
    bgCircle1: {
      position: "absolute",
      top: -20,
      right: -20,
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: "rgba(255,255,255,0.08)",
    },
    bgCircle2: {
      position: "absolute",
      bottom: -28,
      left: 20,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: "rgba(255,255,255,0.05)",
    },
    topInner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      position: "relative",
    },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: "rgba(255,255,255,0.20)",
      borderWidth: 1,
      borderColor: "rgba(255,255,255,0.35)",
      alignItems: "center",
      justifyContent: "center",
    },
    avatarText: {
      color: "#ffffff",
      fontSize: 13,
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    headInfo: { flex: 1, minWidth: 0 },
    nameStack: { position: "relative", minHeight: 30 },
    skeletonOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
    },
    skeletonOverlayRight: { left: undefined, width: "60%" },
    skeletonName: { height: 12, width: "75%" },
    skeletonEmail: { height: 10, width: "100%" },
    name: {
      color: "#ffffff",
      fontSize: 13,
      fontWeight: "700",
      letterSpacing: -0.1,
    },
    usernameChip: {
      alignSelf: "flex-start",
      backgroundColor: "rgba(255,255,255,0.22)",
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: 6,
      marginTop: 4,
    },
    usernameChipText: {
      color: "#ffffff",
      fontSize: 10,
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: 8,
      paddingHorizontal: 12,
    },
    rowDivider: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: t.borderSoft,
    },
    rowLabel: {
      color: t.textMute,
      fontSize: 9,
      fontWeight: "700",
      letterSpacing: 0.4,
    },
    rowValue: {
      color: t.text,
      fontSize: 11,
      fontWeight: "600",
      maxWidth: "100%",
      textAlign: "right",
    },
    emailValueWrap: {
      position: "relative",
      flex: 1,
      marginLeft: 12,
      minHeight: 14,
      alignItems: "flex-end",
      justifyContent: "center",
    },
  });

export default NtBrandSwitch;
